import React, { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

const BookingModal = ({
  isOpen,
  onClose,
  onConfirm,
  priceId,
  childrenCount = 0,
  currency = "USD",
  price = 0,
  transferType = "rent",
  additionalOptions = [],
}) => {
  const [pickupSign, setPickupSign] = useState("")
  const [additionalAddress, setAdditionalAddress] = useState("")
  const [childrenAges, setChildrenAges] = useState(Array(childrenCount).fill(""))
  const [comment, setComment] = useState("")

  const showPickup = additionalOptions.includes("pickup")
  const showAdditionalAddress = additionalOptions.includes("additional_address")
  const showComment = additionalOptions.includes("comment")
  const hasPickupSection = showPickup || showAdditionalAddress

  const handleConfirm = () => {
    onConfirm({
      transfer_price_id: priceId,
      ...(showPickup ? { pickup_sign: pickupSign } : {}),
      ...(showAdditionalAddress ? { additional_address: additionalAddress } : {}),
      children_ages: childrenAges,
      ...(showComment ? { comment } : {}),
    })
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/50" onClick={onClose} />

          <motion.div
            className="relative bg-white rounded-t-2xl sm:rounded-xl border border-gray-100 shadow-sm w-full sm:max-w-lg overflow-hidden"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {/* header */}
            <div className="flex items-start justify-between px-5 pt-5 pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-semibold text-app-base-strong leading-tight">
                  Complete your booking
                </h3>
                <p className="text-[10px] font-medium text-app-content-faint uppercase tracking-wider mt-1">
                  {transferType === "group" ? "Group" : "Individual"} transfer
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-app-content-faint hover:text-app-content transition-colors mt-0.5"
              >
                <XIcon />
              </button>
            </div>

            {/* body */}
            <div className="px-5 py-4 flex flex-col gap-5 max-h-[60vh] overflow-y-auto">
              {/* pickup */}
              {hasPickupSection && (
                <section>
                  <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider mb-3">
                    Pickup details
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {showPickup && (
                      <Field label="Pick up sign">
                        <input
                          type="text"
                          placeholder="Name shown on driver's board"
                          value={pickupSign}
                          onChange={(e) => setPickupSign(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong placeholder:text-app-content-faint outline-none focus:border-app-base-strong/40 transition-colors"
                        />
                      </Field>
                    )}
                    {showAdditionalAddress && (
                      <Field label="Additional address">
                        <input
                          type="text"
                          placeholder="Extra drop-off or pickup point"
                          value={additionalAddress}
                          onChange={(e) => setAdditionalAddress(e.target.value)}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong placeholder:text-app-content-faint outline-none focus:border-app-base-strong/40 transition-colors"
                        />
                      </Field>
                    )}
                  </div>
                </section>
              )}

              {/* children ages — только если есть дети */}
              {childrenCount > 0 && (
                <section>
                  <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider mb-3">
                    Children ages
                  </p>
                  <div className="flex flex-col gap-2">
                    {childrenAges.map((age, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs text-app-content-faint w-14 shrink-0">
                          Child {i + 1}
                        </span>
                        <div className="flex-1 relative">
                          <select
                            value={age}
                            onChange={(e) => {
                              const updated = [...childrenAges]
                              updated[i] = e.target.value
                              setChildrenAges(updated)
                            }}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong outline-none focus:border-app-base-strong/40 appearance-none bg-white transition-colors"
                          >
                            <option value="">Select age</option>
                            {Array.from({ length: 12 }, (_, j) => (
                              <option key={j + 1} value={j + 1}>
                                {j + 1} {j === 0 ? "year" : "years"}
                              </option>
                            ))}
                          </select>
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                            <ChevronDown size={14} />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* comment */}
              {showComment && (
                <section>
                  <p className="text-[10px] font-semibold text-app-content-faint uppercase tracking-wider mb-3">
                    Comment
                  </p>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Flight number, special requests, luggage info…"
                    rows={3}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-app-base-strong placeholder:text-app-content-faint outline-none focus:border-app-base-strong/40 resize-none transition-colors"
                  />
                </section>
              )}

              {/* summary */}
              <div className="bg-gray-50/70 border border-gray-100 rounded-xl px-4 py-3">
                <div className="flex justify-between text-xs text-app-content-faint mb-2">
                  <span>Transfer</span>
                  <span>
                    {currency} {price.toFixed(0)}
                  </span>
                </div>
                {transferType === "group" && (
                  <div className="flex justify-between text-xs text-app-content-faint mb-2">
                    <span>Pricing</span>
                    <span>per group</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2.5 flex justify-between items-center">
                  <span className="text-sm font-semibold text-app-base-strong">Total</span>
                  <span className="text-xl font-bold text-app-base-strong tracking-tight">
                    {currency} {price.toFixed(0)}
                  </span>
                </div>
              </div>
            </div>

            {/* footer */}
            <div className="px-5 py-4 border-t border-gray-100 flex gap-2.5">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 text-sm font-medium text-app-content border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <motion.button
                onClick={handleConfirm}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="flex-[2] py-2.5 text-sm font-semibold text-white bg-app-base-strong hover:bg-app-base rounded-xl transition-colors shadow-sm shadow-app-base-strong/25"
              >
                Book now
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs text-app-content-faint font-medium">{label}</span>
    {children}
  </label>
)

const XIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const ChevronDown = ({ size = 16 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

export default BookingModal
